import type { ReactNode } from 'react'
import { YStack, XStack, Text, Anchor, styled } from 'tamagui'
import { FooterSection } from '../components/sections'
import {
  LOGO_ALT,
  LOGO_SIZE_LEGAL_HEADER,
  LOGO_WORDMARK_SRC,
} from '../lib/logo'

const Page = styled(YStack, {
  name: 'LegalPage',
  flex: 1,
  backgroundColor: '$background',
})

const Header = styled(XStack, {
  name: 'LegalHeader',
  paddingHorizontal: '$6',
  paddingVertical: '$5',
  borderBottomWidth: 1,
  borderBottomColor: '$border',
  alignItems: 'center',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$4',
  },
})

const Content = styled(YStack, {
  name: 'LegalContent',
  maxWidth: 900,
  width: '100%',
  marginHorizontal: 'auto',
  paddingHorizontal: '$6',
  paddingVertical: '$10',
  gap: '$8',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$8',
  },
})

const Title = styled(Text, {
  name: 'LegalTitle',
  // @ts-ignore - Tamagui's `render` controls the underlying HTML element on web
  render: 'h1',
  fontFamily: '$heading',
  fontSize: '$10',
  fontWeight: '700',
  color: '$textPrimary',
  lineHeight: '$10',

  '$md': {
    fontSize: '$8',
    lineHeight: '$8',
  },
})

const SectionHeading = styled(Text, {
  name: 'LegalSectionHeading',
  // @ts-ignore - Tamagui's `render` controls the underlying HTML element on web
  render: 'h2',
  fontFamily: '$heading',
  fontSize: '$6',
  fontWeight: '600',
  color: '$textPrimary',
  marginBottom: '$3',
})

const Body = styled(Text, {
  name: 'LegalBody',
  fontFamily: '$body',
  fontSize: '$4',
  color: '$textSecondary',
  lineHeight: '$6',
})

const BulletRow = styled(XStack, {
  name: 'LegalBulletRow',
  gap: '$3',
  alignItems: 'flex-start',
})

const BulletDot = styled(Text, {
  name: 'LegalBulletDot',
  fontFamily: '$body',
  fontSize: '$4',
  color: '$orangePrimary',
  lineHeight: '$6',
})

export function LegalPageLayout({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <Page>
      <Header>
        <Anchor href="/" textDecorationLine="none">
          <img
            src={LOGO_WORDMARK_SRC}
            alt={LOGO_ALT}
            height={LOGO_SIZE_LEGAL_HEADER}
            style={{
              height: LOGO_SIZE_LEGAL_HEADER,
              width: 'auto',
              cursor: 'pointer',
            }}
          />
        </Anchor>
      </Header>
      <Content>
        <Title>{title}</Title>
        {children}
      </Content>
      <FooterSection />
    </Page>
  )
}

export function LegalSection({
  heading,
  children,
}: {
  heading: string
  children: ReactNode
}) {
  return (
    <YStack gap="$3">
      <SectionHeading>{heading}</SectionHeading>
      {children}
    </YStack>
  )
}

export function LegalParagraph({ children }: { children: ReactNode }) {
  return <Body>{children}</Body>
}

export function LegalBulletList({ items }: { items: ReactNode[] }) {
  return (
    <YStack gap="$3">
      {items.map((item, i) => (
        <BulletRow key={i}>
          <BulletDot>•</BulletDot>
          <Body flex={1}>{item}</Body>
        </BulletRow>
      ))}
    </YStack>
  )
}
